import type { ColDef } from 'ag-grid-community';

/**
 * Baseline column defaults for `AgGridBase.Table` (merged before per-grid `defaultColDef` / `agGridProps.defaultColDef`).
 * Feature grids can override individual flags per column or via `defaultColDef`.
 */
export const DEFAULT_BASE_COL_DEF: ColDef = {
  minWidth: 50,
  resizable: true,
  sortable: true,
  filter: false,
  floatingFilter: false,
  suppressHeaderContextMenu: true,
  suppressHeaderMenuButton: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

/** Viewport and row chrome used by `AgGridBase.Table` when `bodyHeight` / `bodyMinHeight` / header & row heights are not overridden. */
export const DEFAULT_GRID_SIZING = {
  defaultHeight: '37.5rem',
  minHeight: '200px',
  headerHeight: 40,
  rowHeight: 50,
} as const;

/**
 * Default columns tool panel (left side). Passed to AG Grid as `sideBar` when `showSidebar` is true.
 * Consumers can replace via `agGridProps.sideBar` while keeping `showSidebar={true}`.
 */
export const DEFAULT_GRID_SIDEBAR = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressPivots: true,
        suppressPivotMode: true,
        suppressRowGroups: true,
        suppressValues: true,
      },
    },
  ],
  hiddenByDefault: false,
  position: 'left',
};

export const DEFAULT_URL_SYNC_DEBOUNCE_MS = 200;

/** Defaults applied when `AgGridBase.Table` `pagination` is enabled. */
export const DEFAULT_PAGINATION = {
  pageSize: 50,
  pageSizeSelector: [25, 50, 100] as const,
  /** Keep one SSRM block in memory so page changes do not retain scrolled pages. */
  maxBlocksInCache: 1,
} as const;

export type AgGridBasePaginationConfig = {
  /** Rows per page. Also used as SSRM `cacheBlockSize` when `dataMode` is `server`. */
  pageSize?: number;
  /** Page-size dropdown values, `true` for AG Grid defaults, or `false` to hide the selector. */
  pageSizeSelector?: number[] | boolean;
  /** SSRM only. Limits how many row blocks stay cached while paginating. */
  maxBlocksInCache?: number;
  /** Built-in pagination panel sections and their order. */
  panels?: Array<'pageSize' | 'rowSummary' | 'pageSummary'>;
};

export type UrlParams = Record<string, string | string[] | undefined>;

/** Current state of the grid based on sort/filter params from the URL. */
export type GridUrlState = {
  filterModel: Record<string, unknown>;
  sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>;
  q?: string;
};

/**
 * Used to convert the URL params to the grid state and vice versa.
 */
export type UrlSyncAdapter = {
  fromUrl: (urlParams: UrlParams) => GridUrlState;
  toUrl: (gridState: GridUrlState) => UrlParams;
};

export type AgGridBaseUrlSyncConfig = {
  /**
   * When true, the URL sync is enabled.
   */
  enabled?: boolean;
  /**
   * The adapter to use for the URL sync.
   */
  adapter: UrlSyncAdapter;
  preserveKeys?: string[];
  debounceMs?: number;
  replaceHistory?: boolean;
  setUrlState?: (nextParams: UrlParams, opts?: { replace?: boolean }) => void;
  getUrlState?: () => UrlParams;
  /**
   * When true, defers hydration until URL state is ready (e.g. useSearchParamsState).
   */
  isUrlLoading?: boolean;
  /**
   * Current URL-derived state (e.g. `filter` from useSearchParamsState). Used to detect external
   * URL changes (back/forward) after the grid is ready.
   */
  urlStateRevision?: unknown;
};
